import { trackerStatuses, campaignPaymentStatuses } from 'constants/statuses';
import { calculateCampaignDates } from 'utils/calculateCampaignDates';

/**
 * Parse GraphQL Campaign data to match the portal's expected format
 * This transforms the C# PascalCase GraphQL response to the JavaScript camelCase format
 * expected by the existing campaign components
 */
export const parseCampaignFromGraphQL = (campaign) => {
  if (!campaign) return null;

  // Extract customer information from customerCampaigns junction table (if available)
  const customerCampaign = campaign.customerCampaigns?.[0] || campaign.customerCampaignsForCustomer?.[0];
  const customerLogin = customerCampaign?.customerLogin;
  const customer = customerCampaign?.customerLogin?.customer;

  const openCancellations = campaign.cancellations?.filter((cancellation) => {
    const statusName = cancellation.cancellationStatusNavigation?.statusName?.toLowerCase();
    // Only include active/open statuses (requested, pending, in progress, etc.)
    return statusName && !['completed', 'cancelled', 'rejected', 'closed', 'denied'].includes(statusName);
  }) || [];

  // Get the most recent open cancellation (sort by createDate descending)
  const mostRecentOpenCancellation = openCancellations.length > 0
    ? openCancellations.sort((a, b) => new Date(b.createDate) - new Date(a.createDate))[0]
    : null;

  const continuityProgramCancellation = mostRecentOpenCancellation
    ? {
      status: mostRecentOpenCancellation.cancellationStatusNavigation?.statusName?.toLowerCase() || null,
      cancelReasons: mostRecentOpenCancellation.cancelReasons,
      cancelText: mostRecentOpenCancellation.cancelText,
      name: mostRecentOpenCancellation.name,
      phone: mostRecentOpenCancellation.phone,
      email: mostRecentOpenCancellation.email,
      createDate: mostRecentOpenCancellation.createDate,
      statusName: mostRecentOpenCancellation.cancellationStatusNavigation?.statusName
    }
    : null;

  // Get status for date calculations
  const statusId = campaign.status?.id || campaign.statusId;
  const statusName = campaign.status?.statusName;

  const { startDate: calculatedStartDate, endDate: calculatedEndDate } = calculateCampaignDates({
    campaign: {
      id: campaign.id,
      statusId,
      signedDate: campaign.signedDate,
      estimatedStartDate: campaign.estimatedStartDate,
      estimatedEndDate: campaign.estimatedEndDate,
      campaignDuration: campaign.campaignDuration,
      bonusMonths: campaign.bonusMonths,
      evergreen: campaign.evergreen,
      evergreenCancelDate: campaign.evergreenCancelDate
    },
    campaignKiosks: [], // Kiosks not available in list view, will use estimated dates
    defaultCampaignDurationDays: 45, // Default value
    paidEvergreenBills: 0 // Evergreen data not available in list view
  });

  // Use calculated dates, fallback to estimated dates if calculation fails
  // Convert Date objects to ISO strings for consistency
  const startDate = calculatedStartDate 
    ? calculatedStartDate.toISOString() 
    : (campaign.estimatedStartDate ? `${campaign.estimatedStartDate}T00:00:00.000Z` : null);
  const endDate = calculatedEndDate 
    ? calculatedEndDate.toISOString() 
    : (campaign.estimatedEndDate ? `${campaign.estimatedEndDate}T00:00:00.000Z` : null);

  // Build the campaign object in the expected format
  // For list view, we only need: id, name, status, dates
  return {
    id: campaign.id,
    name: campaign.name,
    orderNumber: campaign.orderNumber || '-',
    status: statusId,
    statusId,
    statusName,
    statusDescription: statusName,

    // Dates - only startDate and endDate (no estimated/actual split)
    createdDate: campaign.createdDate,
    startDate,
    endDate,

    // Billing (not available in Campaign model directly, calculated from transactions)
    billingStatus: campaign.billingStatus || null,

    // Meta
    // In the customer portal we treat all existing campaigns as view-only entries.
    // Previously, statusId = 1 was mapped to "draft" and caused navigation to `/new-campaign`.
    // That behavior is no longer desired, so we explicitly disable draft handling here.
    isDraftCampaign: false,

    // Optional fields from detailed queries
    signedDate: campaign.signedDate,
    lastUpdated: campaign.lastUpdated,
    evergreen: campaign.evergreen,
    evergreenCancelDate: campaign.evergreenCancelDate,
    campaignDuration: campaign.campaignDuration,
    bonusMonths: campaign.bonusMonths,
    estimatedStartDate: campaign.estimatedStartDate,
    estimatedEndDate: campaign.estimatedEndDate,
    genericArtwork: campaign.genericArtwork,
    timeSensative: campaign.timeSensative,
    takedownPriorityDays: campaign.takedownPriorityDays,
    contractedKioskTotal: campaign.contractedKioskTotal,
    surplusKioskTotal: campaign.surplusKioskTotal,

    // Customer information
    companyId: customer?.id,
    companyName: customer?.companyName,
    customerEmail: customerLogin?.email,
    customerFirstName: customerLogin?.firstName,
    customerLastName: customerLogin?.lastName,
    customerPhone: customer?.phone,

    // Cancellation information (from new schema)
    continuityProgramCancellation,

    // Payment method information (extract from campaignPaymentMethods)
    paymentMethodId: campaign.campaignPaymentMethods?.[0]?.customerPaymentMethodId || null,
    secondaryPaymentMethodId: campaign.campaignPaymentMethods?.[1]?.customerPaymentMethodId || null,

    // Determine payment status based on whether a payment method is set
    paymentStatus: campaign.campaignPaymentMethods?.[0]?.customerPaymentMethodId 
      ? campaignPaymentStatuses.SUBMITTED 
      : campaignPaymentStatuses.NOT_SUBMITTED,

    // Placeholder fields - these will be fetched separately
    kiosks: [],
    payment: {},
    salesRep: null,
    artDesigner: null,
    customerCare: null,
    marketingSpecialist: null,
    telemarketer: null,
    admins: [],
    workflow: {
      art_design: {},
      location_signoff: {}
    },
    artPreferences: {},
    designArts: [],
    statusHistory: [],
    transactions: [],
    artChangeRequests: [],

    isEvergreenPayment: campaign.evergreen || false
  };
};

/**
 * Batch parse multiple campaigns from GraphQL
 */
export const parseCampaignsFromGraphQL = (campaigns) => {
  if (!Array.isArray(campaigns)) return [];
  return campaigns.map(parseCampaignFromGraphQL).filter(Boolean);
};
